import { useMemo } from "react";
import { Accordion } from "@base-ui/react/accordion";
import { IconChevronDown } from "@tabler/icons-react";
import type { SelectedCafe } from "./MapView";
import { getOpeningHoursStatus, getOpeningHoursWeekTable } from "../lib/opening-hours";

type OpeningHoursAccordionProps = {
  cafe: SelectedCafe | null;
  simulatedDate: Date;
};

function formatStatusSummary(
  state: "open" | "closed" | "unknown" | "invalid" | "unavailable",
  nextChange: Date | null,
  fallbackLabel: string,
): string {
  if (state === "open") {
    if (nextChange) {
      return `Open until ${nextChange.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    }
    return "Open now";
  }

  if (state === "closed") {
    if (nextChange) {
      return `Closed until ${nextChange.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    }
    return "Closed now";
  }

  if (state === "unknown") {
    return "Hours uncertain";
  }

  return fallbackLabel;
}

export function OpeningHoursAccordion({ cafe, simulatedDate }: OpeningHoursAccordionProps) {
  const openingStatus = useMemo(() => {
    if (!cafe) {
      return null;
    }
    return getOpeningHoursStatus(cafe.openingHours, simulatedDate, {
      coordinates: cafe.coordinates,
      countryCode: cafe.countryCode,
      state: cafe.state,
    });
  }, [cafe, simulatedDate]);

  const openingTable = useMemo(() => {
    if (!cafe) {
      return null;
    }
    return getOpeningHoursWeekTable(cafe.openingHours, simulatedDate, {
      coordinates: cafe.coordinates,
      countryCode: cafe.countryCode,
      state: cafe.state,
    });
  }, [cafe, simulatedDate]);

  if (!cafe) {
    return null;
  }

  if (!openingTable) {
    if (!cafe.openingHours.trim()) {
      return null;
    }
    return (
      <p className="mt-1 text-sm text-slate-700">
        Opening hours: {cafe.openingHours}
      </p>
    );
  }

  return (
    <Accordion.Root className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70" defaultValue={[]}>
      <Accordion.Item value="opening-hours">
        <Accordion.Header>
          <Accordion.Trigger className="group flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm text-slate-800">
            <span className="min-w-0">
              <span className="block font-medium">Opening hours</span>
              <span className="mt-0.5 block text-xs text-slate-600">
                {openingStatus
                  ? formatStatusSummary(openingStatus.state, openingStatus.nextChange, openingStatus.label)
                  : "Unknown"}
              </span>
            </span>
            <IconChevronDown
              size={16}
              stroke={2.25}
              className="shrink-0 text-slate-500 transition-transform duration-200 group-data-panel-open:rotate-180"
            />
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Panel className="border-t border-slate-200 px-3 pb-2 pt-2">
          <table className="w-full border-collapse text-sm text-slate-700">
            <tbody>
              {openingTable.rows.map(row => (
                <tr key={row.dayLabel} className={row.isReferenceDay ? "font-semibold text-slate-900" : undefined}>
                  <td className="w-28 py-0.5 pr-2 align-top">{row.dayLabel}</td>
                  <td className="py-0.5">
                    {row.ranges.map((range, index) => (
                      <span key={`${row.dayLabel}-${range}-${index}`} className="block">
                        {range}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {openingTable.ignoredPH ? (
            <p className="mt-1 text-xs text-slate-500">Public holidays are not available for this place, so PH was ignored.</p>
          ) : null}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion.Root>
  );
}
